import React, { useEffect, useState, useRef } from 'react';
import { Table, Typography, Button, Tag, Input, DatePicker, Space, message, Tooltip, Grid, FloatButton } from 'antd';
import { PlusOutlined, EyeOutlined, FileTextOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ResponsiveTable from '../../components/Common/ResponsiveTable';

const { Title } = Typography;
const { useBreakpoint } = Grid;

const csvEscape = (value) => {
  const raw = value == null ? '' : String(value);
  const escaped = raw.replace(/"/g, '""');
  return `"${escaped}"`;
};

const buildCsv = (headers, rows) => {
  const headerLine = headers.map(csvEscape).join(',');
  const dataLines = rows.map((r) => r.map(csvEscape).join(','));
  return `\uFEFF${[headerLine, ...dataLines].join('\r\n')}`;
};

const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const UserNominas = () => {
  const { isReadOnly } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = useRef(null);

  // Filtros hacia backend
  const [uuidFilter, setUuidFilter] = useState('');
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const buildFilters = (uuid, range) => {
    const f = {};
    if (uuid) f.uuid = uuid;
    if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
      f.date_from = range[0].format('YYYY-MM-DD');
      f.date_to = range[1].format('YYYY-MM-DD');
    }
    return f;
  };

  const fetchNominas = async (filters = {}) => {
    setLoading(true);
    try {
      const response = await userService.getNominas(1, null, { ...filters, limit: 10 });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching nóminas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNominas();
  }, []);

  const downloadResponseFile = (response, defaultFilename) => {
    const contentType = response.headers['content-type'] || 'application/octet-stream';
    const disposition = response.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] || defaultFilename;

    let blob;
    const data = response.data;
    if (data instanceof Blob) {
      blob = data;
    } else if (data instanceof ArrayBuffer) {
      blob = new Blob([data], { type: contentType });
    } else {
      blob = new Blob([data], { type: contentType });
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',')
      ? base64Content.split(',').pop()
      : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleQuickDownloadXml = async (record) => {
    try {
      const resp = await userService.downloadInvoiceXml(record.id);
      const prefix = record?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${prefix}${record?.serie || ''}_${record?.folio || ''}_${record?.uuid || ''}.xml`;
      downloadResponseFile(resp, filename);
    } catch (err) {
      message.error('No se pudo descargar el XML.');
    }
  };

  const handleQuickDownloadPdf = async (record) => {
    try {
      const result = await userService.generateInvoicePdf(record.id, {});
      const base64 = result?.data?.pdf_content;
      const prefix = record?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${prefix}${record?.serie || ''}_${record?.folio || ''}_${record?.uuid || ''}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      message.error('No se pudo generar/descargar el PDF.');
    }
  };

  const handleUuidChange = (e) => {
    const value = e.target.value;
    setUuidFilter(value);
    fetchNominas(buildFilters(value, dateRange));
  };

  const handleDateChange = (values) => {
    const range = values || [];
    setDateRange(range);
    fetchNominas(buildFilters(uuidFilter, range));
  };

  const exportNominasCsv = async () => {
    setExportingCsv(true);
    try {
      const filters = buildFilters(uuidFilter, dateRange);
      const pageSize = 100;
      const all = [];
      let page = 1;
      let total = null;

      while (true) {
        const resp = await userService.getNominas(page, null, { ...filters, limit: pageSize });
        const list = resp?.data?.invoices || [];
        if (total == null) total = resp?.data?.total ?? list.length;
        all.push(...list);
        if (list.length === 0) break;
        if (all.length >= total) break;
        page += 1;
        if (page > 200) break;
      }

      if (all.length === 0) {
        message.info('No hay nóminas para exportar con los filtros actuales.');
        return;
      }

      const headers = [
        'Serie',
        'Folio',
        'UUID',
        'RFC Empleado',
        'Empleado',
        'Total',
        'Estatus',
        'Fecha creación',
      ];

      const rows = all.map((row) => [
        row?.serie || '',
        row?.folio || '',
        row?.uuid || row?.folio_fiscal || '',
        row?.receiver?.tax_id || '',
        row?.receiver?.tax_name || '',
        Number(row?.total || 0).toFixed(2),
        row?.status || '',
        row?.created_at ? new Date(row.created_at).toISOString() : '',
      ]);

      const csv = buildCsv(headers, rows);
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      downloadCsv(csv, `nominas_${stamp}.csv`);
      message.success(`CSV exportado (${all.length} nóminas).`);
    } catch (e) {
      message.error('No se pudo exportar el CSV.');
    } finally {
      setExportingCsv(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'timbrada': return 'success';
      case 'cancelada': return 'error';
      case 'borrador': return 'warning';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'timbrada': return 'Timbrada';
      case 'cancelada': return 'Cancelada';
      case 'borrador': return 'Borrador';
      default: return 'Desconocido';
    }
  };

  const columns = [
    {
      title: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 120,
    },
    {
      title: 'Serie',
      dataIndex: 'serie',
      key: 'serie',
      width: 120,
    },
    {
      title: 'Empleado',
      dataIndex: 'client_name',
      key: 'client_name',
      ellipsis: true,
      width: 200,
      render: (_, row) => screens.xs ? (row.receiver?.tax_id || 'N/A') : `${row.receiver?.tax_id || 'N/A'} (${row.receiver?.tax_name || 'N/A'})`,
    },
    {
      title: 'Folio fiscal',
      dataIndex: 'uuid',
      key: 'uuid',
      width: 220,
      render: (uuid) => uuid || '—',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (total) => `$${parseFloat(total || 0).toFixed(2)}`,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalle">
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                if (record.status === 'borrador') {
                  navigate('/nominas/create', { state: { invoiceId: record.id } });
                } else {
                  navigate(`/invoices/${record.id}`);
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Descargar XML">
            <Button
              shape="circle"
              size="small"
              icon={<FileTextOutlined />}
              disabled={record.status !== 'timbrada'}
              onClick={() => handleQuickDownloadXml(record)}
            />
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <Button
              shape="circle"
              size="small"
              type="primary"
              icon={<FilePdfOutlined />}
              disabled={record.status !== 'timbrada'}
              onClick={() => handleQuickDownloadPdf(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ].filter(item => !screens.xs || ['client_name', 'uuid', 'status', 'actions'].includes(item.key));

  return (
    <div style={{ padding: screens.xs ? '12px' : '24px' }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: screens.xs ? 'column' : 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        alignItems: screens.xs ? 'stretch' : 'center',
        gap: '12px',
        marginBottom: screens.xs ? '16px' : '24px'
      }}>
        <Title level={2} style={{ margin: 0, fontSize: screens.xs ? '20px' : '24px' }}>
          Mis Nóminas
        </Title>
        <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: screens.xs ? '100%' : 'auto' }}>
          <Button icon={<DownloadOutlined />} onClick={exportNominasCsv} loading={exportingCsv} block={screens.xs}>
            Exportar CSV
          </Button>
          {!isReadOnly && <Button
            type="primary"
            icon={<PlusOutlined />}
            size={screens.xs ? 'middle' : 'large'}
            onClick={() => navigate('/nominas/create')}
            block={screens.xs}
          >
            Nueva Nómina
          </Button>}
        </Space>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Space wrap style={{ width: '100%' }} direction={screens.md ? 'horizontal' : 'vertical'} size={screens.xs ? 'small' : 'middle'}>
          <Input
            placeholder="Buscar por UUID"
            value={uuidFilter}
            onChange={handleUuidChange}
            allowClear
            style={{ width: screens.md ? 280 : '100%' }}
            size={screens.xs ? 'small' : 'middle'}
          />
          <DatePicker.RangePicker 
            onChange={handleDateChange} 
            style={{ width: screens.md ? 'auto' : '100%' }}
            size={screens.xs ? 'small' : 'middle'}
          />
          <Button 
            onClick={() => { setUuidFilter(''); setDateRange([]); fetchNominas(); }}
            block={!screens.md}
            size={screens.xs ? 'small' : 'middle'}
          >
            Limpiar filtros
          </Button>
        </Space>
      </div>

      <div ref={topPaginationRef} />
      <ResponsiveTable
        columns={columns}
        dataSource={invoices}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} nóminas`,
        }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
};

export default UserNominas;
